import React, { memo, useEffect, useRef, useState } from 'react'

import ColorPicker from 'components/basic/color-picker/ColorPicker'
import { DatasetDropdown } from 'components/basic/dropdowns/dataset/DatasetDropdown'
import { easasAlert } from 'components/fragments/easas-global-fragment/EasasGlobalAlerts'
import { Button } from 'primereact/button'
import { Column, ColumnBodyOptions } from 'primereact/column'
import { confirmPopup } from 'primereact/confirmpopup'
import { DataTable } from 'primereact/datatable'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { T } from 'services/lang/LanguageService'
import useScadaStore from 'store/scada-store'
import { ScadaEditorProps, ScadaLabel, ScadaProps } from '../scadaTypes'
import { defaultLabel } from '../utils/default-values'
import ScadaTagEditor from './ScadaTagEditor'

type ScadaDatasetsTableProps = ScadaEditorProps & {
  currentConfig: ScadaProps,
  setCurrentConfig: React.Dispatch<React.SetStateAction<ScadaProps>>,
}

const ScadaDatasetsTable: React.FC<ScadaDatasetsTableProps> = ({
  currentConfig,
  setCurrentConfig,
  onConfigChange
}) => {
  const [scroll, setScroll] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [labelId, setLabelId] = useState<string | null>(null);
  const addLabelRef = useRef<HTMLDivElement>(null);
  const updateScadaLabels = useScadaStore(state => state.updateScadaLabels);

  useEffect(() => {
    if (scroll) {
      addLabelRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    setScroll(false);
  }, [scroll]);

  const elements = [...currentConfig.elements];

  const updateElementsOnStates = (newElements: ScadaLabel[]) => {
    const updConfig = {
      ...currentConfig,
      elements: newElements,
    };
    updateScadaLabels(currentConfig.id!, newElements);
    setCurrentConfig(updConfig);
    onConfigChange(updConfig);
  };

  const handleDatasetChange = (value: string | number, option: ColumnBodyOptions) => {
    const dataset = elements[option.rowIndex];
    const updDataset = {
      ...dataset,
      [option.field]: value,
    };
    elements.splice(option.rowIndex, 1, updDataset);

    updateElementsOnStates(elements);
  };

  const handleEdit = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, label: ScadaLabel) => {
    e.preventDefault();
    setLabelId(label.identifier);
    setDialogVisible(true);
  };

  const confirmOnDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
    e.preventDefault();
    confirmPopup({
      target: e.currentTarget,
      message: T("confirm-dialog.are-you-sure-you-want-to-delete-this-label"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => deleteColumn(index),
    });
  };

  const deleteColumn = (index: number) => {
    elements.splice(index, 1);
    updateElementsOnStates(elements);
  };

  const addLabel = (identifier: string | null, label: string | null) => {
    if (!identifier || !label) return;
    if (isIdentifierExists(identifier, label)) return;
    const newElements: ScadaLabel[] = [
      ...elements,
      {
        ...defaultLabel,
        identifier,
      },
    ];

    updateElementsOnStates(newElements);
    setScroll(true);
    easasAlert({
      summary: T("alert.success"),
      severity: "success",
      detail: T("alert.label-was-added", { label }),
    });
  };

  const isIdentifierExists = (identifier: string, label: string) => {
    const identifiers = elements.map(e => e.identifier);
    if (identifiers?.includes(identifier)) {
      easasAlert({
        summary: T("alert.warn-label-exists"),
        detail: T("alert.label-already-exists-in-the-list", { label }),
        severity: "warn",
      });
      return true;
    }
    return false;
  };

  const updateLabel = (identifier: string | null, label: string | null, index: number) => {
    if (!identifier || !label) return;
    if (isIdentifierExists(identifier, label)) return;
    const updatedElements = elements.map((e, i) => (i === index ? { ...e, identifier } : e));

    updateElementsOnStates(updatedElements ?? []);

    easasAlert({
      summary: T("alert.updated"),
      detail: T("alert.label-was-updated-to", { label }),
      severity: "success",
    });
  };

  const renderDatasetField = (data: ScadaLabel, option: ColumnBodyOptions) => (
    <DatasetDropdown
      value={data.identifier}
      onChange={(id, label) => updateLabel(id, label, option.rowIndex)}
    />
  );

  const renderXCoordField = (data: ScadaLabel, option: ColumnBodyOptions) => (
    <InputNumber
      style={{ minWidth: "4rem" }}
      value={data.xCoord}
      onChange={e => handleDatasetChange(e.value!, option)}
      maxFractionDigits={1}
      max={100}
      min={0}
    />
  );

  const renderYCoordField = (data: ScadaLabel, option: ColumnBodyOptions) => (
    <InputNumber
      style={{ minWidth: "4rem" }}
      value={data.yCoord}
      onChange={e => handleDatasetChange(e.value!, option)}
      maxFractionDigits={1}
      max={100}
      min={0}
    />
  );

  const renderFontSizeField = (data: ScadaLabel, option: ColumnBodyOptions) => (
    <InputNumber
      className="justify-self-end"
      style={{ minWidth: "3rem" }}
      value={data.fontSize}
      onChange={e => handleDatasetChange(e.value!, option)}
      max={120}
      min={1}
    />
  );

  const renderLayerField = (data: ScadaLabel, option: ColumnBodyOptions) => (
    <Dropdown
      value={data.layer}
      onChange={e => handleDatasetChange(e.value, option)}
      style={{
        width: "max(8rem, 10rem)",
        height: "3rem",
        padding: "0",
      }}
      className="p-inputtext-sm align-items-center"
      options={[...currentConfig.labelLayers.map(lg => lg.layer)]}
    />
  );

  const renderBgColorField = (data: ScadaLabel, option: ColumnBodyOptions) => (
    <ColorPicker
      value={data.bgColor}
      onValueChange={e => handleDatasetChange(e.target.value, option)}
    />
  );

  const renderActionButtons = (label: ScadaLabel, option: ColumnBodyOptions) => (
    <span className="p-buttonset">
      <Button
        icon="pi pi-pencil"
        className="p-button-sm p-button-secondary p-button-text w-2rem px-0 py-2"
        onClick={e => handleEdit(e, label)}
      />
      <Button
        icon="pi pi-trash"
        className="p-button-sm p-button-danger p-button-text w-2rem px-0 py-2"
        onClick={e => confirmOnDelete(e, option.rowIndex)}
      />
    </span>
  );

  return (
    <>
      <DataTable value={elements} size="small" className="w-full mt-3">
        <Column
          field="identifier"
          header={T("word.dataset")}
          className="w-full gap-3"
          body={renderDatasetField}
        />
        <Column
          field="xCoord"
          header={`${T("word.position")} X, %`}
          className="text-center"
          body={renderXCoordField}
        />
        <Column
          field="yCoord"
          header={`${T("word.position")} Y, %`}
          className="text-center"
          body={renderYCoordField}
        />
        <Column field="fontSize" header={T("word.font-size")} body={renderFontSizeField} />
        <Column field="layer" header={T("word.layer")} body={renderLayerField} />
        <Column field="bgColor" header={T("word.background-color")} body={renderBgColorField} />
        <Column style={{ width: "0" }} body={renderActionButtons} />
      </DataTable>
      <div ref={addLabelRef} className="flex mt-3 w-full">
        <DatasetDropdown onChange={addLabel} />
      </div>
      <Dialog
        header={T("word.tag")}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        modal
        resizable
        draggable
      >
        <ScadaTagEditor
          tag={elements.find(el => el.identifier === labelId)}
          onClose={() => setDialogVisible(false)}
          config={currentConfig}
          onConfigChange={onConfigChange}
        />
      </Dialog>
    </>
  );
}

export default memo(ScadaDatasetsTable);